/**
 * F3 debug overlay. This is a development tool, not a HUD - it prints exactly
 * the numbers you need when tuning drunkenness or chasing a determinism bug.
 *
 * Labels are Finnish like the rest of the UI, but the terms the README itself
 * uses in English - fps, frame, sway, yaw/pitch, simulationTime - stay as they
 * are. Translating those would only make the tool harder to read against the
 * code they come from.
 */

import { BEER } from '../config/beer';
import { MOMENTUM } from '../config/gameplay';
import { describeIntoxication, type IntoxicationLevel } from '../config/intoxication';
import { formatSpeedKmh, formatTime } from '../utils/format';
import type { RunState } from '../game/GameState';
import type { PlayerState } from '../player/PlayerMovement';
import type { IntoxicationSample } from '../player/IntoxicationSystem';
import type { CheckpointSpec } from '../world/Level';
import type { SampleStatus } from '../audio/AudioManager';
import { el, setHidden } from './dom';

export interface DebugSnapshot {
  fps: number;
  frameSeconds: number;
  stepsLastFrame: number;
  accumulator: number;
  simulationTime: number;
  elapsedMs: number;
  state: RunState;
  player: PlayerState;
  speed: number;
  intox: IntoxicationSample;
  nextCheckpoint: CheckpointSpec | null;
  timeToNextStumble: number;
  collidersVisible: boolean;
  colliderCount: number;
  samples: SampleStatus;
  intoxication: IntoxicationLevel;
  alcoholStrength: number;
  drugStrength: number;
  hannu: { phase: string; distance: number; swipes: number };
}

const f = (value: number, digits = 2): string => value.toFixed(digits).padStart(digits + 4, ' ');

export class DebugOverlay {
  readonly root = el('div', 'debug');
  private visible = false;

  mount(parent: HTMLElement): void {
    parent.appendChild(this.root);
    setHidden(this.root, true);
  }

  setVisible(visible: boolean): void {
    this.visible = visible;
    setHidden(this.root, !visible);
  }

  get isVisible(): boolean {
    return this.visible;
  }

  update(s: DebugSnapshot): void {
    if (!this.visible) return;
    const p = s.player;
    const lines = [
      `LANIAAMU DEBUG            F3 piilota  F4 colliderit`,
      ``,
      `fps            ${f(s.fps, 1)}   frame ${f(s.frameSeconds * 1000, 2)} ms`,
      `askelta/frame  ${String(s.stepsLastFrame).padStart(6, ' ')}   akkum ${f(s.accumulator * 1000, 3)} ms`,
      `simulationTime ${f(s.simulationTime, 4)} s`,
      `runin kello    ${formatTime(s.elapsedMs)}`,
      `tila           ${s.state}`,
      ``,
      `sijainti       ${f(p.position.x)} ${f(p.position.y)} ${f(p.position.z)}`,
      `nopeusvektori  ${f(p.velocity.x)} ${f(p.velocity.y)} ${f(p.velocity.z)}`,
      `vauhti         ${f(s.speed)} m/s = ${formatSpeedKmh(s.speed)}   maassa ${p.grounded ? 'on ' : 'ei '}`,
      `vauhtikerroin  ${f(p.momentum, 3)} / ${MOMENTUM.max.toFixed(2)}   puhdasta ${f(p.cleanSeconds, 1)} s`,
      `kalja          ${String(p.beer.sips).padStart(6, ' ')} huikkaa   boost +${f(
        p.beer.boost,
        3,
      )} / ${BEER.maxBoost.toFixed(2)}   cd ${f(p.beer.cooldown, 2)} s`,
      `yaw / pitch    ${f(p.yaw, 3)} / ${f(p.pitch, 3)} rad`,
      ``,
      `maapito        ${f(s.intox.groundGrip, 3)}`,
      `kameratehoste  ${f(s.intox.stumbleIntensity, 3)}   seuraava ${
        Number.isFinite(s.timeToNextStumble) ? f(s.timeToNextStumble, 2) : '  n/a'
      } s`,
      `huikkasuunta   ${f(p.beer.direction.x, 3)} ${f(p.beer.direction.y, 3)}   voima ${f(
        p.beer.strength,
        3,
      )}   ${f(p.beer.timer, 2)} s jaljella`,
      `ohjaussuunta   ${f(p.steerDir.x, 3)} ${f(p.steerDir.y, 3)}`,
      `kamera y/p/r   ${f(s.intox.cameraYaw, 3)} ${f(s.intox.cameraPitch, 3)} ${f(s.intox.cameraRoll, 3)}`,
      ``,
      `annos          ${describeIntoxication(s.intoxication)}`,
      `kalja x        ${f(s.alcoholStrength, 3)}   happo x ${f(s.drugStrength, 3)}`,
      `hannu          ${s.hannu.phase.padEnd(10, ' ')} ${f(s.hannu.distance, 1)} m  ${s.hannu.swipes} sivallusta`,
      ``,
      `seuraava cp    ${s.nextCheckpoint ? s.nextCheckpoint.id : 'VALMIS'}`,
      `colliderit     ${s.colliderCount} (${s.collidersVisible ? 'näkyvissä' : 'piilossa'})`,
      `samplet        ${s.samples.ready}/${s.samples.total} valmiina${
        s.samples.failed > 0 ? `   ${s.samples.failed} EI LATATTU` : ''
      }`,
    ];
    this.root.textContent = lines.join('\n');
  }
}
